'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import {
  Search,
  Send,
  CheckCheck,
  User,
  Calendar,
  Award,
  Sparkles,
  Clock,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

interface ChatMessage {
  id: string;
  sender: 'client' | 'business';
  senderType?: string;
  text: string;
  time: string;
  status?: string;
}

interface ChatContact {
  id: string;
  clientId?: string | null;
  name: string;
  phone: string;
  avatarBg: string;
  lastMessage: string;
  lastTime: string;
  unreadCount?: number;
  appointmentsCount: number;
  points: number;
}

const AVATAR_COLORS = ['#047857', '#0284c7', '#7c3aed', '#db2777', '#d97706'];

export function WhatsAppChatsInbox() {
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 1. Cargar lista de conversaciones desde PostgreSQL
  const loadConversations = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      const url = searchTerm.trim()
        ? `/api/whatsapp/chats?search=${encodeURIComponent(searchTerm.trim())}`
        : '/api/whatsapp/chats';
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        const convs: any[] = data.conversations || [];
        const formatted: ChatContact[] = convs.map((c, i) => ({
          id: c.id,
          clientId: c.clientId,
          name: c.name,
          phone: c.phone,
          avatarBg: AVATAR_COLORS[i % AVATAR_COLORS.length],
          lastMessage: c.lastMessage || 'Sin mensajes aún',
          lastTime: c.lastTime || '',
          unreadCount: c.unreadCount || 0,
          appointmentsCount: c.appointmentsCount || 0,
          points: c.points || 0
        }));

        setContacts(formatted);
        if (isInitial && formatted.length > 0 && !selectedContactId) {
          setSelectedContactId(formatted[0].id);
        }
      }
    } catch (err) {
      console.warn('Error loading conversations:', err);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [searchTerm, selectedContactId]);

  // Carga inicial y por cambio de búsqueda
  useEffect(() => {
    loadConversations(true);
  }, [loadConversations]);

  // 2. Cargar mensajes de la conversación seleccionada
  const loadMessages = useCallback(async (convId: string, silent = false) => {
    if (!convId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const res = await fetch(`/api/whatsapp/chats/${convId}/messages`);
      const data = await res.json();
      if (data.success) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.warn('Error loading chat messages:', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (selectedContactId) {
      loadMessages(selectedContactId);
    }
  }, [selectedContactId, loadMessages]);

  // 3. Polling en segundo plano cada 3.5 segundos (actualización en vivo sin recargar)
  useEffect(() => {
    const timer = setInterval(() => {
      loadConversations(false);
      if (selectedContactId) {
        loadMessages(selectedContactId, true);
      }
    }, 3500);

    return () => clearInterval(timer);
  }, [selectedContactId, loadConversations, loadMessages]);

  // Scroll automático hacia el último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const activeContact = contacts.find((c) => c.id === selectedContactId) || contacts[0];

  // 4. Envío manual de respuestas desde el dashboard
  const handleSendMessage = async (textToSend?: string) => {
    const text = textToSend || replyText;
    if (!text.trim() || !activeContact || sending) return;

    setSending(true);
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg: ChatMessage = {
      id: tempId,
      sender: 'business',
      text: text.trim(),
      time: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sent'
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    if (!textToSend) setReplyText('');

    try {
      const res = await fetch(`/api/whatsapp/chats/${activeContact.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() })
      });
      const data = await res.json();
      if (data.success && data.message) {
        // Reemplazar mensaje temporal con el confirmado de base de datos
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, id: data.message.id, status: data.message.status } : m))
        );
      }
      loadConversations(false);
    } catch (err) {
      console.error('Error enviando mensaje manual:', err);
    } finally {
      setSending(false);
    }
  };

  const quickReplies = [
    '👍 ¡Perfecto, te esperamos!',
    '📍 Estamos en Av. Santa Fe 1234, CABA',
    '⏳ ¿Podrías avisarnos 15 min antes si tenés alguna demora?',
    '✂️ Turno actualizado en el sistema.'
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col md:flex-row h-[720px] max-w-6xl mx-auto">
      {/* 1. Columna Izquierda: Contactos de WhatsApp */}
      <div className="w-full md:w-80 border-r border-slate-200/90 flex flex-col bg-slate-50/50 shrink-0">
        {/* Header buscador */}
        <div className="p-3.5 border-b border-slate-200 bg-white">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar chat por cliente o teléfono..."
              className="w-full pl-9 pr-3 py-2 bg-slate-100 hover:bg-slate-200/60 focus:bg-white border border-transparent focus:border-emerald-600 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none transition"
            />
          </div>
        </div>

        {/* Lista de hilos */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
              <span>Cargando conversaciones...</span>
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-slate-400 space-y-2">
              <MessageSquare className="w-8 h-8 mx-auto text-slate-300 stroke-1" />
              <p className="text-xs font-medium text-slate-600">No hay chats activos</p>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Los mensajes entrantes de WhatsApp se recibirán aquí en tiempo real.
              </p>
            </div>
          ) : (
            contacts.map((c) => {
              const isSelected = c.id === activeContact?.id;
              return (
                <div
                  key={c.id}
                  onClick={() => setSelectedContactId(c.id)}
                  className={`p-3.5 flex items-start gap-3 cursor-pointer transition ${
                    isSelected
                      ? 'bg-emerald-50/70 border-l-4 border-[#047857]'
                      : 'hover:bg-slate-100/70'
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 shadow-2xs"
                    style={{ backgroundColor: c.avatarBg }}
                  >
                    {c.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-900 truncate">
                        {c.name}
                      </span>
                      <span className="text-[10px] text-slate-400 font-medium">
                        {c.lastTime}
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                      {c.lastMessage}
                    </p>

                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800">
                        WhatsApp
                      </span>
                      {c.unreadCount && c.unreadCount > 0 ? (
                        <span className="w-4 h-4 rounded-full bg-[#047857] text-white text-[9px] font-bold flex items-center justify-center">
                          {c.unreadCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 2. Columna Centro: Conversación en Vivo de WhatsApp */}
      {activeContact ? (
        <div className="flex-1 flex flex-col bg-[#efeae2]/30 min-w-0">
          {/* Cabecera del Chat */}
          <div className="p-3.5 px-5 bg-white border-b border-slate-200 flex items-center justify-between shadow-2xs">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-2xs"
                style={{ backgroundColor: activeContact.avatarBg }}
              >
                {activeContact.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  {activeContact.name}
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                </h3>
                <p className="text-[11px] text-slate-500">
                  +{activeContact.phone} · En línea por WhatsApp
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link
                href="/dashboard/clients"
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-700 transition flex items-center gap-1"
              >
                <User className="w-3.5 h-3.5 text-slate-500" />
                <span>Ver ficha CRM</span>
              </Link>
            </div>
          </div>

          {/* Área de Mensajes (Burbujas WhatsApp) */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 bg-[#f0f2f5]/40">
            {loadingMessages ? (
              <div className="p-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                <span>Cargando mensajes...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs">
                No hay mensajes aún en esta conversación.
              </div>
            ) : (
              messages.map((msg) => {
                const isBusiness = msg.sender === 'business';
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isBusiness ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[78%] sm:max-w-[65%] rounded-2xl p-3 text-xs shadow-2xs leading-relaxed ${
                        isBusiness
                          ? 'bg-[#dcf8c6] text-slate-900 rounded-tr-none border border-emerald-200/50'
                          : 'bg-white text-slate-900 rounded-tl-none border border-slate-200/80'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-slate-400">
                        <span>{msg.time}</span>
                        {isBusiness && (
                          <CheckCheck className={`w-3.5 h-3.5 ${msg.status === 'read' ? 'text-sky-500' : 'text-emerald-700'}`} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Respuestas Rápidas Predefinidas */}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-1.5 overflow-x-auto text-[11px]">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
              Rápido:
            </span>
            {quickReplies.map((qr) => (
              <button
                key={qr}
                type="button"
                onClick={() => handleSendMessage(qr)}
                className="px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:border-emerald-600 hover:bg-emerald-50 text-slate-700 text-[10px] font-semibold shrink-0 transition cursor-pointer"
              >
                {qr}
              </button>
            ))}
          </div>

          {/* Barra inferior de envío */}
          <div className="p-3 bg-white border-t border-slate-200 flex items-center gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendMessage();
              }}
              placeholder="Escribe un mensaje de WhatsApp a este cliente..."
              className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200/60 focus:bg-white border border-transparent focus:border-emerald-600 rounded-2xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none transition"
            />
            <button
              type="button"
              onClick={() => handleSendMessage()}
              disabled={!replyText.trim() || sending}
              className="w-10 h-10 rounded-2xl bg-[#047857] hover:bg-[#065f46] text-white flex items-center justify-center transition shadow-sm disabled:opacity-40 cursor-pointer shrink-0"
              title="Enviar por WhatsApp"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-xs">
          Selecciona un chat para ver los mensajes.
        </div>
      )}

      {/* 3. Columna Derecha: Resumen Rápido del Cliente */}
      {activeContact && (
        <div className="hidden lg:flex w-64 border-l border-slate-200 p-4 flex-col gap-4 bg-white shrink-0">
          <div className="text-center pt-2">
            <div
              className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white font-extrabold text-xl shadow-sm"
              style={{ backgroundColor: activeContact.avatarBg }}
            >
              {activeContact.name.charAt(0).toUpperCase()}
            </div>
            <h4 className="font-bold text-sm text-slate-900 mt-2">
              {activeContact.name}
            </h4>
            <p className="text-xs text-slate-500">+{activeContact.phone}</p>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3.5 space-y-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span>Turnos totales</span>
              </span>
              <span className="font-bold text-slate-900">
                {activeContact.appointmentsCount}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-500 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-emerald-600" />
                <span>Puntos fidelidad</span>
              </span>
              <span className="font-black text-emerald-700">
                {activeContact.points} pts
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-500 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>Asistencia</span>
              </span>
              <span className="font-bold text-sky-700">100% puntual</span>
            </div>
          </div>

          <div className="p-3 bg-emerald-50/60 border border-emerald-100 rounded-2xl text-[11px] text-emerald-900">
            <span className="font-bold flex items-center gap-1 text-[#6255F5] mb-1">
              <Sparkles className="w-3 h-3 text-[#6255F5]" />
              Calenddy Copilot
            </span>
            <p className="leading-relaxed">
              Conversación sincronizada con Evolution API. Los mensajes entrantes se vinculan automáticamente con la ficha del cliente.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
